import {
  useContext,
  computed,
  reactive,
  useRoute,
} from "@nuxtjs/composition-api";
import { useVuelidate } from "@vuelidate/core";

import { helpers } from "@vuelidate/validators";
import RFT_SSR from "./rftSSR.graphql";
import CITY_BY_ID from "./cityById.graphql";
import PROFESSION_BY_ID from "./professionById.graphql";
import PROFESSION_QUESTIONS from "./professionQuestions.json";
import NEW_TENDER_MUTATION from "~/pages/request-for-tender/newTender.graphql";
import ATTACH_PLAN_TO_TENDER from "~/composables/designer/attachPlanToTender.graphql";
import { useAuthStore } from "~/components/_refactored/auth";

const MIN_DESCRIPTION_LENGTH = 10;
const MAX_DESCRIPTION_LENGTH = 5000;
const TENDER_DATA_KEY = "jszk-tender";
const VERSION = "1.0.0";
export const JOSZAKI_TOKENKEY = "jszk_token-key";

const formData = reactive({
  name: "",
  profession: null,
  jobDescription: "",
  city: null,
  email: "",
  phone: {
    number: "",
    countryCode: null,
  },
  terms: false,
  /**
   * Map of slug → answer snapshot for dynamic custom-question modules.
   * Each snapshot stores enough metadata to render the original question
   * in support tools without joining back to the module version.
   * Shape: { slug, label, description, questionType, answer, position }
   */
  moduleAnswers: {},
  moduleVersionId: null,
  // Originating URL of the tender (e.g. a landing page). Persisted server-side
  // as `from_url` for source attribution / Redash reporting.
  fromUrl: null,
  // Set when the tender originates from a designer plan. After the tender is
  // created the plan is attached to it via attachPlanToTender.
  // Shape: { planId, designerId } | null
  planContext: null,
});

const state = reactive({
  mate: null,
  prefilled: false,
  modalOpen: false,
  loading: false,
  showFloater: true,
  step: 1,
});

function reset() {
  formData.name = "";
  formData.profession = null;
  formData.jobDescription = "";
  formData.city = null;
  formData.email = "";
  formData.phone = {
    number: "",
    countryCode: null,
  };
  formData.terms = false;
  formData.moduleAnswers = {};
  formData.moduleVersionId = null;
  formData.fromUrl = null;
  formData.planContext = null;
}

function setPlanContext(planContext) {
  formData.planContext = planContext;
}

function open() {
  state.modalOpen = true;
}

function close() {
  state.modalOpen = false;
}

function hideFloater() {
  state.showFloater = false;
}

function showFloater() {
  state.showFloater = true;
}

function getTenderTokenKey() {
  return localStorage.getItem(JOSZAKI_TOKENKEY);
}

function modifyCityAndProfession() {
  state.prefilled = false;
}

export function useNewTender() {
  const {
    $validation: { rules },
    $helpers,
    $query,
    $cookie,
    $mutate,
    $track,
    $trackers,
    $logger,
    i18n,
    store,
  } = useContext();
  const route = useRoute();
  const authStore = useAuthStore();

  function saveToStorage() {
    if (process.client) {
      console.debug("saving to cookie", getPayload());
      localStorage.setItem(
        TENDER_DATA_KEY,
        JSON.stringify({
          ...getPayload(),
          __version: VERSION,
        })
      );
    } else {
      throw new Error("saveToStorage is only available on client side");
    }
  }

  async function loadFromStorage() {
    if (process.server) {
      throw new Error("loadFromStorage is only available on client side");
    }
    const data = localStorage.getItem(TENDER_DATA_KEY);
    console.debug("loading data from storage", data);
    if (data) {
      const parsed = JSON.parse(data);
      if (parsed.__version !== VERSION) {
        localStorage.removeItem(TENDER_DATA_KEY);
        return;
      }

      const cityQueryId = route.value.query.cityId;
      const professionQueryId = route.value.query.professionId;
      console.debug("loadFromStorage parsed", parsed);
      formData.name = parsed.name;
      formData.jobDescription = parsed.description;
      formData.email = parsed.email;
      formData.terms = parsed.terms;
      formData.phone.number = parsed.phone;
      // Restore previously-saved custom-question answers, but ONLY when the
      // stored snapshot was written against the same module version that's
      // currently active. If the admin published a new version while the
      // user was away, the saved slugs/types may no longer match — better
      // to drop them than to submit stale data tied to a now-different
      // versionId.
      const storedAnswers = parsed.moduleAnswers;
      const storedVersionId = storedAnswers?.moduleVersionId || null;
      const currentVersionId = formData.moduleVersionId;
      const versionsCompatible =
        !currentVersionId ||
        !storedVersionId ||
        storedVersionId === currentVersionId;
      if (
        storedAnswers &&
        Array.isArray(storedAnswers.answers) &&
        versionsCompatible
      ) {
        const map = {};
        for (const ans of storedAnswers.answers) {
          if (ans?.slug) map[ans.slug] = ans;
        }
        formData.moduleAnswers = map;
        formData.moduleVersionId = storedVersionId;
      }

      const promises = [];
      if (
        parsed.cityId &&
        formData.city?.id !== parsed.cityId &&
        !cityQueryId
      ) {
        promises.push($query(CITY_BY_ID, { cityId: parsed.cityId }));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (
        parsed.professionId &&
        formData.profession?.id !== parsed.professionId &&
        !professionQueryId
      ) {
        promises.push(
          $query(PROFESSION_BY_ID, { professionId: parsed.professionId })
        );
      } else {
        promises.push(Promise.resolve(null));
      }

      const [city, profession] = await Promise.all(promises);

      if (
        parsed.phoneCountryCodeId &&
        formData.phone.countryCode?.id !== parsed.phoneCountryCodeId
      ) {
        const countryCode = store.getters["data/phoneCountryCodes"].find(
          // Json automatically converts id to number
          // eslint-disable-next-line eqeqeq
          (cc) => cc.id == parsed.phoneCountryCodeId
        );
        formData.phone = {
          number: parsed.phone,
          countryCode,
        };
      } else {
        formData.phone.number = parsed.phone;
      }

      if (city?.city && !formData.city) {
        formData.city = city.city;
      }
      if (profession?.professionById && !formData.profession) {
        formData.profession = profession.professionById;
      }
    }
  }

  const profession = computed({
    get() {
      return formData.profession;
    },
    set(value) {
      formData.profession = value;
    },
  });

  const jobDescription = computed({
    get() {
      return formData.jobDescription;
    },
    set(value) {
      if (!value) {
        formData.jobDescription = "";
        return;
      }
      const newValue = value.substring(0, MAX_DESCRIPTION_LENGTH);
      formData.jobDescription =
        newValue?.length > 2 ? $helpers.capitalize(newValue) : newValue;
    },
  });

  const descriptionHint = computed(() => {
    if (formData.profession) {
      return PROFESSION_QUESTIONS[formData.profession.seoName];
    }
    return [];
  });

  const city = computed({
    get() {
      return formData.city;
    },
    set(value) {
      formData.city = value;
    },
  });

  const name = computed({
    get() {
      return formData.name;
    },
    set(value) {
      formData.name = value?.length > 2 ? $helpers.capitalize(value) : value;
    },
  });

  const phone = computed({
    get() {
      return formData.phone;
    },
    set(value) {
      formData.phone = value;
    },
  });

  const email = computed({
    get() {
      return formData.email;
    },
    set(value) {
      formData.email = value;
    },
  });

  const terms = computed({
    get() {
      return formData.terms;
    },
    set(value) {
      formData.terms = value;
    },
  });

  function prefill(data) {
    // formData is a module singleton, so a plan context left over from an
    // earlier designer flow would leak into this tender. Flows that do come
    // from a plan call setPlanContext after prefill.
    formData.planContext = null;
    loadFromStorage();
    // { profession, city, mate, description }
    profession.value = data.profession;
    city.value = data.city;
    state.mate = data.mate;
    state.prefilled = !!city.value && !!profession.value;
    if (data.description) {
      jobDescription.value = data.description;
    }
  }

  async function prefillWithIds() {
    formData.planContext = null;
    const personId = route.value.query.personId ?? "0";
    const cityId = route.value.query.cityId;
    const professionId = route.value.query.professionId;

    try {
      if (state.mate?.id === personId) {
        return;
      }
      const resp = await $query(RFT_SSR, {
        mateId: personId,
        cityId,
        professionId,
      });

      if (resp?.personPublicProfile) {
        state.mate = resp.personPublicProfile;
      }
      if (cityId) {
        formData.city = resp?.city;
      }
      if (professionId) {
        formData.profession = resp?.professionById;
      }

      state.prefilled = true;
    } catch (e) {
      console.error(e);
      $logger.error("Error prefillWithIds", e);
    }
  }

  const formRules = {
    name: {
      required: rules.required,
      minLength: rules.minLength(3),
      maxLength: rules.maxLength(25),
    },
    profession: {
      required: rules.required,
    },
    jobDescription: {
      required: rules.required,
      length: helpers.withMessage(
        ({ $model }) =>
          i18n.t("newTender.minDescription", {
            minLength: MIN_DESCRIPTION_LENGTH,
            remaining: MIN_DESCRIPTION_LENGTH - ($model?.length ?? 0),
          }),
        (value) => value.length >= MIN_DESCRIPTION_LENGTH
      ),
    },
    city: {
      required: rules.required,
    },
    email: {
      required: rules.required,
      // The legacy wizard additionally ran a custom lowercase-only regex; the
      // email rule covers format and forceKeyPressLowercase already blocks
      // uppercase at input time, so the regex is intentionally not ported.
      email: rules.email,
      maxLength: rules.maxLength(50),
    },
    terms: {
      required: rules.consentRequired,
    },
    phone: {
      number: {
        required: rules.required,
      },
      countryCode: {
        required: rules.required,
      },
    },
  };

  const personHasSelectedProfession = computed(() => {
    if (!state.mate || !formData.profession || !formData.city) {
      return true;
    }

    const hasPersonProfession =
      state.mate.personProfessions?.some(
        (p) => p.profession?.id === formData.profession?.id
      ) ?? false;

    const hasZoneForCity = state.mate.zones?.some((personZone) => {
      const zoneProfessionIds =
        personZone?.zone?.miniMarket?.professions?.map((p) => p.id) ?? [];

      if (!zoneProfessionIds.includes(formData.profession?.id)) {
        return false;
      }

      return (
        personZone?.zone?.subcounties?.some(
          (subcounty) =>
            subcounty.id === formData.city.subcountyId ||
            (subcounty.countyId === "1" && formData.city.countyId === "1") // ha BP-t választotta, akkor engedjük a BP-s zonákat
        ) ?? false
      );
    });

    return hasPersonProfession || hasZoneForCity;
  });

  const v$ = useVuelidate(formRules, formData);

  function getDropReason() {
    if (!state.mate || personHasSelectedProfession.value) return null;

    const zones = state.mate.zones ?? [];
    if (zones.length === 0) return "no_coverage";

    const hasZoneForProfession = zones.some((personZone) => {
      const zoneProfessionIds =
        personZone?.zone?.miniMarket?.professions?.map((p) => p.id) ?? [];
      return zoneProfessionIds.includes(formData.profession?.id);
    });
    if (!hasZoneForProfession) return "wrong_profession";

    return "wrong_location";
  }

  function setModuleVersionId(id) {
    formData.moduleVersionId = id || null;
  }

  function setFromUrl(url) {
    formData.fromUrl = url || null;
  }

  function setModuleAnswer(slug, snapshot) {
    if (!slug) return;
    formData.moduleAnswers = {
      ...formData.moduleAnswers,
      [slug]: snapshot,
    };
  }

  function clearModuleAnswer(slug) {
    if (!slug || !(slug in formData.moduleAnswers)) return;
    const next = { ...formData.moduleAnswers };
    delete next[slug];
    formData.moduleAnswers = next;
  }

  // Drop every stored custom-question answer. Used when the active module
  // version changes (admin published a new config) so we don't submit stale
  // slug/type pairs that no longer match the live schema.
  function clearAllModuleAnswers() {
    formData.moduleAnswers = {};
  }

  function getModuleAnswersPayload() {
    const map = formData.moduleAnswers || {};
    const snapshots = Object.values(map);
    if (snapshots.length === 0) return null;
    return {
      moduleVersionId: formData.moduleVersionId || null,
      answers: [...snapshots].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
      ),
    };
  }

  function getPayload() {
    const dropped = !!state.mate && !personHasSelectedProfession.value;
    return {
      name: formData.name,
      // forceKeyPressLowercase only guards typed input; pasted text can still
      // carry uppercase, so normalize before sending.
      email: formData.email?.toLowerCase(),
      phone: formData.phone.number,
      phoneCountryCodeId: parseInt(formData.phone.countryCode?.id),
      description: formData.jobDescription,
      cityId: parseInt(formData.city?.id),
      professionId: parseInt(formData.profession?.id),
      mateId:
        state.mate && personHasSelectedProfession.value
          ? parseInt(state.mate.id)
          : null,
      droppedMateId: dropped ? parseInt(state.mate.id) : null,
      dropReason: dropped ? getDropReason() : null,
      campaignsCookie: $cookie.get("campaigns"),
      // Originating URL (landing page); backend persists as `from_url`.
      fromUrl: formData.fromUrl || null,
      // Custom-question answers, snapshotted at submit time. Sent as a JSON
      // scalar and stored in `tenders.module_answers`. Null when the wizard
      // had no custom-question modules.
      moduleAnswers: getModuleAnswersPayload(),
    };
  }

  function trackSubmitJob() {
    $track.analyticsEvent({
      event: "submit_job",
      category: "ajanlatkeres",
      action: "Submit job",
      label: "v2.0",
    });

    $trackers.trackEvent({
      title: "Submit Job",
      data: {
        professionSeoName: formData.profession.seoName,
        professionGroupSeoName: formData.profession?.professionGroup?.seoName,
        citySeoName: formData.city.seoName,
        designerId: formData.planContext?.designerId ?? null,
        planId: formData.planContext?.planId ?? null,
        v: 2,
      },
    });
  }

  function trackJobSubmitted(tender) {
    const tenderId = tender.id;
    $track.analyticsEvent({
      category: "ajanlatkeres",
      action: "normal",
    });

    // The Lead push must precede job_submitted (matching the wizard's
    // submitJob in _activeTab.vue): GTM tags triggered on job_submitted read
    // content.* as persistent data-layer variables, which this push seeds.
    if (profession.value) {
      $track.customEvent({
        event: "Facebook.track",
        content: {
          eventName: "Lead",
          eventData: {
            content_name: profession.value?.seoName,
            content_category: profession.value?.professionGroup?.seoName,
            content_ids: [profession.value?.id],
            content_type: "profession",
          },
          eventMeta: {
            eventID: `tender_${tenderId}`,
          },
        },
      });
    }

    $trackers.trackEvent({
      title: "Job Submitted",
      data: {
        professionSeoName: profession.value?.seoName,
        professionGroupSeoName: profession.value?.professionGroup?.seoName,
        citySeoName: city.value?.seoName,
        personId:
          state.mate && personHasSelectedProfession.value
            ? parseInt(state.mate.id)
            : null,
        personIsActive:
          state.mate && personHasSelectedProfession.value
            ? state.mate?.isActive
            : null,
        designerId: formData.planContext?.designerId ?? null,
        planId: formData.planContext?.planId ?? null,
        tenderProfessionId: tender.profession?.id ?? null,
      },
    });

    $track.analyticsEvent({
      event: "job_submitted",
      category: "ajanlatkeres",
      action: "Job Submitted",
      label: "v1.0",
    });
  }

  async function createTender(skipValidation = false, trackEvent = true) {
    try {
      state.loading = true;
      if (!skipValidation) {
        // Use the closure `v$` rather than `this.v$`. `createTender` is
        // returned from the composable's setup and is regularly invoked as a
        // free function (e.g. destructured), so `this` is not guaranteed to
        // be the component instance that exposes `v$`.
        const valid = await v$.$validate();
        if (!valid) {
          return Promise.reject(new Error("Validation failed"));
        }
      }
      saveToStorage();

      if (trackEvent) {
        trackSubmitJob();
      }

      const { tender, tenderKey, token } = await $mutate(
        NEW_TENDER_MUTATION,
        getPayload()
      );

      if (trackEvent) {
        trackJobSubmitted(tender);
      }

      if (formData.planContext?.planId) {
        try {
          await $mutate(ATTACH_PLAN_TO_TENDER, {
            planId: String(formData.planContext.planId),
            tenderId: String(tender.id),
          });
        } catch (attachError) {
          $logger.error("Failed to attach plan to tender", attachError);
        }
        formData.planContext = null;
      }

      // An already logged-in user must keep their own session: the tender's
      // magic-login token may belong to a different person (resolved by
      // email) and would silently swap or drop the current identity.
      if (!authStore.isAuthenticated) {
        authStore.setToken(token);
      }
      localStorage.setItem(JOSZAKI_TOKENKEY, tenderKey);
      return Promise.resolve({ tender, tenderKey, token });
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    } finally {
      state.loading = false;
    }
  }

  return {
    v$,
    jobDescription,
    profession,
    city,
    name,
    reset,
    state,
    email,
    terms,
    minDescriptionLength: MIN_DESCRIPTION_LENGTH,
    maxDescriptionLength: MAX_DESCRIPTION_LENGTH,
    prefillWithIds,
    personHasSelectedProfession,
    phone,
    formData,
    saveToStorage,
    loadFromStorage,
    createTender,
    getPayload,
    getTenderTokenKey,
    descriptionHint,
    prefill,
    setPlanContext,
    open,
    close,
    modifyCityAndProfession,
    hideFloater,
    showFloater,
    isOpen: computed(() => state.modalOpen),
    moduleAnswers: computed(() => formData.moduleAnswers),
    setModuleVersionId,
    setFromUrl,
    setModuleAnswer,
    clearModuleAnswer,
    clearAllModuleAnswers,
    getModuleAnswersPayload,
  };
}
